/**
 * ErvaPOS - Expenses Management Page
 * Gestão de despesas do negócio
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Loader2,
  Receipt,
  CreditCard,
  Check,
  MoreVertical,
  Zap,
  Droplets,
  Flame,
  Home,
  Phone,
  Wifi,
  Shield,
  Package,
  Wrench,
  MoreHorizontal,
  Calendar,
  Euro,
  TrendingDown,
  Filter,
  ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import api, { expensesAPI } from '../services/api';

const EXPENSE_CATEGORIES = [
  { value: 'electricity', label: 'Eletricidade', icon: Zap, color: 'text-yellow-400' },
  { value: 'water', label: 'Água', icon: Droplets, color: 'text-blue-400' },
  { value: 'gas', label: 'Gás', icon: Flame, color: 'text-orange-400' },
  { value: 'rent', label: 'Renda', icon: Home, color: 'text-purple-400' },
  { value: 'phone', label: 'Telefone', icon: Phone, color: 'text-green-400' },
  { value: 'internet', label: 'Internet', icon: Wifi, color: 'text-cyan-400' },
  { value: 'insurance', label: 'Seguro', icon: Shield, color: 'text-emerald-400' },
  { value: 'supplies', label: 'Consumíveis', icon: Package, color: 'text-slate-400' },
  { value: 'maintenance', label: 'Manutenção', icon: Wrench, color: 'text-rose-400' },
  { value: 'other', label: 'Outros', icon: MoreHorizontal, color: 'text-slate-400' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'card', label: 'Cartão' },
  { value: 'transfer', label: 'Transferência' },
  { value: 'direct_debit', label: 'Débito Direto' },
  { value: 'check', label: 'Cheque' },
];

export const ExpensesPage = () => {
  const ocrFileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [filterDateMode, setFilterDateMode] = useState('month');
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [saving, setSaving] = useState(false);

  const [expenseForm, setExpenseForm] = useState({
    category: '',
    description: '',
    amount: '',
    vat_amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    due_date: '',
    supplier_name: '',
    supplier_nif: '',
    reference: '',
    notes: '',
  });

  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [readingOCR, setReadingOCR] = useState(false);

  const handleExpenseOCR = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    try {
      setReadingOCR(true);
      const { data } = await api.post('/expenses/ocr', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExpenseForm(prev => ({ ...prev, ...data, amount: data.amount || '', vat_amount: data.vat_amount || '', category: data.category || 'other' }));
      setShowCreateModal(true);
      if (data.possible_duplicate) toast.error('Possível duplicado detetado. O sistema impedirá que seja novamente guardado.');
      else toast.success('Fatura lida. Confirme os dados antes de guardar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível ler a fatura');
    } finally {
      setReadingOCR(false);
      if (ocrFileRef.current) ocrFileRef.current.value = '';
    }
  };
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterPaid !== 'all') params.paid = filterPaid === 'paid';

      if (filterDateMode === 'month' && filterMonth) {
        params.date_from = `${filterMonth}-01`;
        const [year, month] = filterMonth.split('-');
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        params.date_to = `${filterMonth}-${String(lastDay).padStart(2, '0')}`;
      }

      if (filterDateMode === 'range') {
        if (filterDateFrom) params.date_from = filterDateFrom;
        if (filterDateTo) params.date_to = filterDateTo;
      }
      
      const [expensesRes, summaryRes, dashRes] = await Promise.all([
        expensesAPI.getAll(params),
        expensesAPI.getSummary(params),
        api.get('/expenses/dashboard'),
      ]);
      setExpenses(expensesRes.data || []);
      setSummary(summaryRes.data || []);
      setDashboard(dashRes.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterPaid, filterDateMode, filterMonth, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredExpenses = expenses.filter(e => {
    if (!searchQuery) return true;
    return (
      e.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.supplier_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.reference?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const createExpense = async () => {
    if (!expenseForm.category || !expenseForm.description || !expenseForm.amount) {
      toast.error('Preencha categoria, descrição e valor');
      return;
    }
    setSaving(true);
    try {
      const { data: savedExpense } = await expensesAPI.create({
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        vat_amount: parseFloat(expenseForm.vat_amount || 0),
      });
      const savedDate = String(savedExpense?.expense_date || expenseForm.expense_date || '');
      const savedMonth = savedDate.slice(0, 7);
      const needsFilterChange = (
        filterDateMode !== 'month'
        || (savedMonth && filterMonth !== savedMonth)
        || filterCategory !== 'all'
        || filterPaid === 'paid'
      );

      // A despesa deve permanecer visível depois de guardada. Uma fatura de um
      // mês anterior desaparecia porque a listagem continuava presa ao mês atual.
      setFilterDateMode('month');
      if (savedMonth) setFilterMonth(savedMonth);
      setFilterCategory('all');
      setFilterPaid('all');

      toast.success(
        savedMonth && savedMonth !== filterMonth
          ? `Despesa registada. A listar o mês da fatura (${savedMonth}).`
          : 'Despesa registada'
      );
      setShowCreateModal(false);
      resetForm();
      // Se os filtros não mudarem, o useEffect não volta a carregar a lista.
      if (!needsFilterChange) loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar despesa');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setExpenseForm({
      category: '',
      description: '',
      amount: '',
      vat_amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      due_date: '',
      supplier_name: '',
      supplier_nif: '',
      reference: '',
      notes: '',
    });
  };

  const handleMarkPaid = async () => {
    if (!selectedExpense) return;
    setSaving(true);
    try {
      await expensesAPI.markPaid(selectedExpense.id, paymentMethod);
      toast.success('Despesa marcada como paga');
      setShowPayModal(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return EXPENSE_CATEGORIES.find(c => c.value === categoryValue) || EXPENSE_CATEGORIES[9];
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalPaid = expenses.filter(e => e.is_paid).reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalPending = expenses.filter(e => !e.is_paid).reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Despesas</h1>
          <p className="text-slate-400">Gestão de despesas do negócio</p>
        </div>
        <div className="flex gap-2">
          <input ref={ocrFileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleExpenseOCR(e.target.files?.[0])} />
          <Button variant="outline" disabled={readingOCR} onClick={() => ocrFileRef.current?.click()} className="border-blue-500/50 text-blue-300">
            {readingOCR ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />} OCR Despesa
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="new-expense-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Despesa
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dark p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-500/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Total Mês</p>
              <p className="text-xl font-bold text-white">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Pagas</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(totalPaid)}</p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Por Pagar</p>
              <p className="text-xl font-bold text-amber-400">{formatCurrency(totalPending)}</p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Categorias</p>
              <p className="text-xl font-bold text-white">{summary.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Categories Summary */}
      {summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {summary.slice(0, 5).map((cat) => {
            const catInfo = getCategoryInfo(cat.category);
            const Icon = catInfo.icon;
            return (
              <Card key={cat.category} className="card-dark p-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${catInfo.color}`} />
                  <span className="text-xs text-slate-400 truncate">{catInfo.label}</span>
                </div>
                <p className="text-lg font-bold text-white mt-1">{formatCurrency(cat.total)}</p>
                <p className="text-xs text-slate-500">{cat.count} registos</p>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Pesquisar despesas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700"
          />
        </div>
        <Select
          value={filterDateMode}
          onValueChange={(value) => setFilterDateMode(value)}
        >
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Mês</SelectItem>
            <SelectItem value="range">Intervalo</SelectItem>
          </SelectContent>
        </Select>

        {filterDateMode === 'month' ? (
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="w-48 bg-slate-800 border-slate-700"
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-44 bg-slate-800 border-slate-700"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-44 bg-slate-800 border-slate-700"
            />
          </div>
        )}
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {EXPENSE_CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPaid} onValueChange={setFilterPaid}>
          <SelectTrigger className="w-36 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="paid">Pagas</SelectItem>
            <SelectItem value="pending">Por pagar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Expenses Table */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Fornecedor</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>IVA</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredExpenses.map((expense) => {
              const catInfo = getCategoryInfo(expense.category);
              const Icon = catInfo.icon;
              return (
                <TableRow key={expense.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${catInfo.color}`} />
                      </div>
                      <span className="text-slate-300 text-sm">{catInfo.label}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white">{expense.description}</p>
                      {expense.reference && (
                        <p className="text-xs text-slate-500">Ref: {expense.reference}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-400">{expense.supplier_name || '-'}</TableCell>
                  <TableCell className="text-slate-400">{expense.expense_date}</TableCell>
                  <TableCell className="text-white font-medium">{formatCurrency(expense.amount)}</TableCell>
                  <TableCell className="text-sky-400 font-medium">{formatCurrency(expense.vat_amount || 0)}</TableCell>
                  <TableCell>
                    {expense.is_paid ? (
                      <Badge className="badge-success">Paga</Badge>
                    ) : (
                      <Badge className="badge-warning">Por Pagar</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!expense.is_paid && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedExpense(expense); setShowPayModal(true); }}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Pagar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredExpenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma despesa encontrada</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Expense Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Despesa</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Categoria</Label>
              <Select
                value={expenseForm.category || "none"}
                onValueChange={(val) => setExpenseForm(prev => ({ ...prev, category: val === "none" ? "" : val }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Selecionar categoria..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecionar...</SelectItem>
                  {EXPENSE_CATEGORIES.map(c => {
                    const Icon = c.icon;
                    return (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${c.color}`} />
                          {c.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Ex: Fatura EDP Março"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Valor total</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="bg-slate-800 border-slate-700 pl-9"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Data Despesa</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, expense_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">IVA</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseForm.vat_amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, vat_amount: e.target.value }))}
                    className="bg-slate-800 border-slate-700 pl-9"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Data Vencimento</Label>
                <Input
                  type="date"
                  value={expenseForm.due_date}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Fornecedor</Label>
              <Input
                value={expenseForm.supplier_name}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, supplier_name: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Nome do fornecedor"
              />
            </div>

            <div>
              <Label className="text-slate-300">NIF do fornecedor</Label>
              <Input value={expenseForm.supplier_nif || ''} onChange={(e) => setExpenseForm(prev => ({ ...prev, supplier_nif: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="NIF" />
            </div>

            <div>
              <Label className="text-slate-300">Referência</Label>
              <Input
                value={expenseForm.reference}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, reference: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Nº da fatura ou referência"
              />
            </div>

            <div>
              <Label className="text-slate-300">Notas</Label>
              <Input
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Observações adicionais"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={createExpense} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Registar Despesa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Marcar Despesa como Paga</DialogTitle>
          </DialogHeader>
          
          {selectedExpense && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400">Despesa</p>
                <p className="text-white font-medium">{selectedExpense.description}</p>
                <p className="text-emerald-400 text-lg font-bold">{formatCurrency(selectedExpense.amount)}</p>
              </div>

              <div>
                <Label className="text-slate-300">Método de Pagamento</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={handleMarkPaid} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
